import { API_QUERY_PARAMS } from '@/types';
import type {
  Church,
  ChurchCreateRequest,
  ChurchListing,
  ChurchUpdateRequest,
  ChurchesResponse,
  GetChurchesParams,
  SearchChurchesParams,
} from '@/types/churches';
import type {
  EvaSyncPreflightRequest,
  EvaSyncPreflightResponse,
  EvaSyncRunCreateRequest,
  EvaSyncRunListing,
  EvaSyncRunView,
} from '@/types/evaSync';
import type {
  ChurchAdmin,
  ChurchAdminCreateRequest,
  ChurchAdminUpdateRequest,
  ChurchGroupListAdmin,
} from '@/types/users';
import { ApiService } from './api.service';

class ChurchesService extends ApiService {
  public async getChurches(params: GetChurchesParams = {}): Promise<ChurchesResponse> {
    const queryParams = new URLSearchParams();

    if (params.term) queryParams.append(API_QUERY_PARAMS.TERM, params.term);
    if (params.cursor) queryParams.append(API_QUERY_PARAMS.CURSOR, params.cursor);
    if (params.limit) queryParams.append(API_QUERY_PARAMS.LIMIT, params.limit.toString());
    if (params.sortBy) queryParams.append(API_QUERY_PARAMS.SORT_BY, params.sortBy);
    if (params.sortOrder) queryParams.append(API_QUERY_PARAMS.SORT_ORDER, params.sortOrder);

    if (params.programs && params.programs.length > 0) {
      params.programs.forEach((program) => {
        queryParams.append(API_QUERY_PARAMS.PROGRAMS, program);
      });
    }

    const url = `/api/v1/churches${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.get<ChurchesResponse>(url);
    return response.data;
  }

  public async getChurch(id: string): Promise<Church> {
    const url = `/api/v1/churches/${id}`;
    const response = await this.get<Church>(url);
    return response.data;
  }

  public async createChurch(churchData: ChurchCreateRequest): Promise<Church> {
    const url = '/api/v1/churches';
    const response = await this.post<Church>(url, churchData);
    return response.data;
  }

  public async updateChurch(id: string, churchData: ChurchUpdateRequest): Promise<Church> {
    const url = `/api/v1/churches/${id}`;
    const response = await this.put<Church>(url, churchData);
    return response.data;
  }

  public async searchChurches(params: SearchChurchesParams = {}): Promise<ChurchListing[]> {
    const queryParams = new URLSearchParams();

    if (params.term) queryParams.append(API_QUERY_PARAMS.TERM, params.term);
    if (params.cluster) queryParams.append(API_QUERY_PARAMS.CLUSTER, params.cluster.toString());
    if (params.diocese) queryParams.append(API_QUERY_PARAMS.DIOCESE, params.diocese.toString());

    const url = `/api/v1/churches/search?${queryParams.toString()}`;
    const response = await this.get<ChurchListing[]>(url);
    return response.data;
  }

  public async getDioceses(params: SearchChurchesParams = {}): Promise<ChurchListing[]> {
    return this.searchChurches({ ...params, diocese: true });
  }

  public async getClusters(params: SearchChurchesParams = {}): Promise<ChurchListing[]> {
    return this.searchChurches({ ...params, cluster: true });
  }

  public async getChurchAdmins(churchId: string): Promise<ChurchAdmin[]> {
    const url = `/api/v1/churches/${churchId}/admins`;
    const response = await this.get<ChurchAdmin[]>(url);
    return response.data;
  }

  public async addChurchAdmin(churchId: string, admin: ChurchAdminCreateRequest): Promise<ChurchAdmin[]> {
    const url = `/api/v1/churches/${churchId}/admins`;
    const response = await this.post<ChurchAdmin[]>(url, admin);
    return response.data;
  }

  public async updateChurchAdmin(
    churchId: string,
    userId: string,
    admin: ChurchAdminUpdateRequest
  ): Promise<ChurchAdmin[]> {
    const url = `/api/v1/churches/${churchId}/admins/${userId}`;
    const response = await this.put<ChurchAdmin[]>(url, admin);
    return response.data;
  }

  public async removeChurchAdmin(churchId: string, userId: string): Promise<void> {
    const url = `/api/v1/churches/${churchId}/admins/${userId}`;
    await this.delete<void>(url);
  }

  public async getChurchGroupListAdmins(churchId: string): Promise<ChurchGroupListAdmin[]> {
    const url = `/api/v1/churches/${churchId}/group-list-admins`;
    const response = await this.get<ChurchGroupListAdmin[]>(url);
    return response.data;
  }

  public async removeChurchGroupListAdminGroup(
    churchId: string,
    userId: string,
    groupId: string
  ): Promise<void> {
    const url = `/api/v1/churches/${churchId}/group-list-admins/${userId}/groups/${groupId}`;
    await this.delete<void>(url);
  }

  public async removeChurchGroupListAdminList(
    churchId: string,
    userId: string,
    listId: string
  ): Promise<void> {
    const url = `/api/v1/churches/${churchId}/group-list-admins/${userId}/lists/${listId}`;
    await this.delete<void>(url);
  }

  public async evaSyncPreflight(
    churchId: string,
    payload: EvaSyncPreflightRequest
  ): Promise<EvaSyncPreflightResponse> {
    const url = `/api/v1/churches/${churchId}/eva-sync/preflight`;
    const response = await this.post<EvaSyncPreflightResponse>(url, payload);
    return response.data;
  }

  public async createEvaSyncRun(churchId: string, payload: EvaSyncRunCreateRequest): Promise<EvaSyncRunView> {
    const url = `/api/v1/churches/${churchId}/eva-sync/runs`;
    const response = await this.post<EvaSyncRunView>(url, payload);
    return response.data;
  }

  public async getEvaSyncRuns(churchId: string): Promise<EvaSyncRunListing[]> {
    const url = `/api/v1/churches/${churchId}/eva-sync/runs`;
    const response = await this.get<EvaSyncRunListing[]>(url);
    return response.data;
  }

  public async getEvaSyncRunReport(churchId: string, runId: string): Promise<Blob> {
    const url = `/api/v1/churches/${churchId}/eva-sync/runs/${runId}/report`;
    return this.getBlob(url);
  }
}

export const churchesService = new ChurchesService();
